import {diagramsApi} from '@dv5/api'
import {DiagramType, useAppDispatch} from '@dv5/domain'
import React, {FC, useEffect, useState} from 'react'
import {ErrorDisplayProcessor} from '../../ErrorDisplayProcessor'
import {MarkdownAtom, MarkdownBlock} from '../../model/markdown/MarkdownText'
import {Button} from '../../inputs/Button'
import {AtomsViewer} from '../atoms/AtomsViewer'
import './PlantUmlCodeViewer.less'

interface PlantUmlCodeViewerProps {
    block: MarkdownBlock
    source: string
    onToggleCheckBox: (atom: MarkdownAtom) => void
}

const imageCache = new Map<string, string>()
let lastManualView: { source: string; view: 'diagram' | 'code' } | null = null

const getCacheKey = (source: string): string => `${DiagramType.PLANTUML}|${source}`

const cacheImage = (key: string, image: string): void => {
    if (imageCache.size >= 50) {
        imageCache.clear()
    }
    imageCache.set(key, image)
}

export const PlantUmlCodeViewer: FC<PlantUmlCodeViewerProps> = ({
                                                                    block,
                                                                    source,
                                                                    onToggleCheckBox,
                                                                }) => {
    const [view, setView] = useState<'diagram' | 'code'>(
        lastManualView && lastManualView.source === source ? lastManualView.view : 'diagram'
    )
    const [renderedImage, setRenderedImage] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [renderError, setRenderError] = useState<string | null>(null)
    const dispatch = useAppDispatch()

    useEffect(() => {
        if (!source) {
            return
        }

        const cacheKey = getCacheKey(source)
        const cachedImage = imageCache.get(cacheKey)
        if (cachedImage) {
            setRenderedImage(cachedImage)
            setRenderError(null)
            setIsLoading(false)
            return
        }

        let cancelled = false
        const timerId = setTimeout(() => {
            setIsLoading(true)
            setRenderError(null)

            const errorProcessor = new ErrorDisplayProcessor(dispatch)
            diagramsApi.render(DiagramType.PLANTUML, source, errorProcessor)
                .then(response => {
                    if (cancelled) return
                    if (response.success) {
                        const image = `data:${response.contentType};base64,${response.data}`
                        cacheImage(cacheKey, image)
                        setRenderedImage(image)
                        setRenderError(null)
                    } else {
                        setRenderError(response.message || 'Failed to render diagram')
                        setRenderedImage(null)
                    }
                })
                .catch(err => {
                    if (cancelled) return
                    setRenderError(typeof err === 'string' ? err : 'Failed to render diagram')
                    setRenderedImage(null)
                })
                .finally(() => {
                    if (!cancelled) setIsLoading(false)
                })
        }, 1000)

        return () => {
            cancelled = true
            clearTimeout(timerId)
        }
    }, [source, dispatch])

    const toggleView = () => {
        const newView: 'diagram' | 'code' = view === 'diagram' ? 'code' : 'diagram'
        setView(newView)
        lastManualView = {source, view: newView}
    }

    return (
        <div className="block code plantuml">
            <div className="plantuml-header">
                <span className="hint">plantuml</span>
                <Button className="plantuml-toggle"
                        title={view === 'diagram' ? 'Show code' : 'Show diagram'}
                        onClick={toggleView}/>
            </div>
            {view === 'diagram' ? (
                <>
                    {isLoading && !renderedImage && (
                        <div className="plantuml-loading">Rendering...</div>
                    )}
                    {renderError && (
                        <div className="plantuml-error">{renderError}</div>
                    )}
                    {renderedImage && (
                        <img className="plantuml-image" src={renderedImage} alt="PlantUML diagram"/>
                    )}
                </>
            ) : (
                <AtomsViewer atoms={block.atoms} onToggleCheckBox={onToggleCheckBox}/>
            )}
        </div>
    )
}